/**
 * The visibility matrix: views across as columns, objects down as rows, one
 * `display: grid` element per row sharing one `gridTemplateColumns`. Mounted by
 * the layout editor's Bulk Edit tab (`BulkEditTab`) and the table settings
 * Visibility tab (`ArTableEditor`); it knows nothing about reducers, providers
 * or Firestore.
 * Two lists must never be swapped: `displayedColumns` is what the grid paints
 * (narrowed by `focusViewId`), `writePool` is the ONLY write pool — a hide
 * composed against the displayed subset would silently drop every omitted view.
 * The grid paints this form type's own and org-wide views until "Show all
 * views" is on, the same affordance `ViewVisibilityControl` offers, and warns
 * whenever a narrowed column set leaves a stored `other` view off screen. A
 * mount that names no form type has no `other` column to reveal, so that switch
 * goes disabled with a tooltip rather than absent.
 * Cell state always comes from `resolveVisibilityCellState` on the RAW stored
 * value, never from a pool-filtered list. Bulk emits one `onBulkChange`, and
 * skips every row that state reports non-editable — a row carrying the stored
 * `hidden` sentinel takes no write here, in bulk or per cell.
 * Only `rowId` is unique — `label` and `groupLabel` both repeat — so every
 * selection aria-label carries it. A cell edits through the same
 * `ViewVisibilityControl` every other visibility surface mounts, so per-cell
 * writes arrive from that control as a whole `ViewVisibility` and only bulk
 * still composes through `composeVisibilityCellWrite`.
 * A failed `parameters/organization` listener degrades the pool to the built-in
 * defaults, so every control goes disabled and an alert says why.
 * Sticky-layer invariant: the header row is the stacking context at z-index 2
 * and its frozen label cell sits above it at 3, while frozen body cells stay at
 * 1 — raise them past 2 and the frozen column punches through the header.
 */
import { FC, useCallback, useState } from 'react';
import { Alert, Checkbox, Switch, Tooltip, Typography, theme } from 'antd';
import type { VisibilityTypeId } from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { useViewVisibilityPool } from '../useViewVisibilityPool';
import { resolveVisibilityCellState } from '../resolveVisibilityCellState';
import { composeVisibilityCellWrite } from '../composeVisibilityCellWrite';
import type { ViewVisibility } from '../ViewVisibility.types';
import { VisibilityMatrixCell } from './VisibilityMatrixCell';
import { VisibilityMatrixHeader } from './VisibilityMatrixHeader';
import { VisibilityMatrixLegend } from './VisibilityMatrixLegend';
import type {
    VisibilityMatrixBulkPlan,
    VisibilityMatrixColumn,
    VisibilityMatrixProps,
} from './VisibilityMatrix.types';

const { Text } = Typography;

/**
 * The one view left showing this object, or `undefined` when more than one view
 * shows it or a locked id (stored but outside the pool) keeps it visible anyway.
 * Hiding that one view is the write `composeVisibilityCellWrite` throws on, so
 * the matrix disables it. Computed once per row, not once per cell.
 */
const onlyShownViewId = (
    value: ViewVisibility,
    writePool: ReadonlySet<VisibilityTypeId>,
): VisibilityTypeId | undefined => {
    const stored = value.visibilityTypes ?? [];
    if (stored.some((id) => !writePool.has(id))) return undefined;
    const shownPool = new Set(stored.length === 0 ? writePool : stored);
    return shownPool.size === 1 ? [...shownPool][0] : undefined;
};

export const VisibilityMatrix: FC<VisibilityMatrixProps> = ({
    rows,
    formTypeId,
    onChange,
    onBulkChange,
    focusViewId,
    onFocusViewChange,
    focusRowId,
    'aria-label': ariaLabel,
}) => {
    const { token } = theme.useToken();
    const { pool, writePool, viewById, loading, error } = useViewVisibilityPool(formTypeId);
    const [selectedRowIds, setSelectedRowIds] = useState<ReadonlySet<string>>(new Set<string>());
    const [showAllViews, setShowAllViews] = useState(false);
    const scrollFocusRowIntoView = useCallback((node: HTMLDivElement | null) => {
        node?.scrollIntoView({ block: 'nearest' });
    }, []);

    const labelColumnWidth = token.controlHeight * 8;
    const viewColumnWidth = token.controlHeight * 4;

    const poolUnavailable = Boolean(error);
    const controlsDisabled = loading === true || poolUnavailable;
    const internalView = viewById.get('internal');
    const writePoolSet = new Set(writePool);

    const allColumns: VisibilityMatrixColumn[] = [
        ...(internalView ? [{ view: internalView, origin: 'org' as const, editable: false }] : []),
        ...pool.map(({ view, origin }) => ({ view, origin, editable: true })),
    ];
    const contextualColumns = showAllViews
        ? allColumns
        : allColumns.filter(({ origin }) => origin !== 'other');
    const focusColumn = allColumns.find(({ view }) => view.id === focusViewId);
    const displayedColumns = focusColumn ? [focusColumn] : contextualColumns;
    const gridTemplateColumns = `${labelColumnWidth}px repeat(${displayedColumns.length}, ${viewColumnWidth}px)`;

    const otherViewIds = new Set(
        pool.filter(({ origin }) => origin === 'other').map(({ view }) => view.id),
    );
    const storedOtherCount = new Set(
        rows
            .flatMap((row) => row.value.visibilityTypes ?? [])
            .filter((id) => otherViewIds.has(id)),
    ).size;

    const groupLabels = Array.from(new Set(rows.map((row) => row.groupLabel)));
    const groups = groupLabels.map((label) => ({
        label,
        rows: rows.filter((row) => row.groupLabel === label),
    }));

    const selectedRows = rows.filter((row) => selectedRowIds.has(row.rowId));
    const selectionLabel =
        selectedRows.length === 1 ? '1 row selected' : `${selectedRows.length} rows selected`;
    const focusStates = focusColumn
        ? rows.map((row) => ({ row, state: resolveVisibilityCellState(row.value, focusColumn.view.id) }))
        : [];
    const hiddenLabels = focusStates.filter(({ state }) => !state.shown).map(({ row }) => row.label);
    const blankLabels = focusStates
        .filter(({ state }) => state.editable && state.memberRows !== 'none')
        .map(({ row }) => row.label);
    const readout = focusColumn
        ? [
            `${focusColumn.view.shortLabel} shows ${focusStates.length - hiddenLabels.length} of ${rows.length}`,
            hiddenLabels.length > 0 ? ` — hidden: ${hiddenLabels.join(', ')}` : ' — nothing hidden',
            blankLabels.length > 0 ? ` · blank on member rows: ${blankLabels.join(', ')}` : '',
        ].join('')
        : [
            `${displayedColumns.length} ${displayedColumns.length === 1 ? 'view' : 'views'} · ${rows.length} ${rows.length === 1 ? 'object' : 'objects'}`,
            selectedRows.length > 0 ? selectionLabel : '',
        ]
            .filter((part) => part.length > 0)
            .join(' · ');

    const toggleRowSelection = (rowId: string) => {
        const next = new Set(selectedRowIds);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        setSelectedRowIds(next);
    };

    const planBulk = (viewId: VisibilityTypeId, shown: boolean): VisibilityMatrixBulkPlan => {
        const hiddenColumnRows = selectedRows.filter(
            (row) => !resolveVisibilityCellState(row.value, viewId).editable,
        );
        const hiddenColumnIds = new Set(hiddenColumnRows.map((row) => row.rowId));
        const candidates = selectedRows.filter((row) => !hiddenColumnIds.has(row.rowId));
        const alreadyInState = candidates.filter(
            (row) => resolveVisibilityCellState(row.value, viewId).shown === shown,
        );
        const pending = candidates.filter(
            (row) => resolveVisibilityCellState(row.value, viewId).shown !== shown,
        );
        const lastShown = pending.filter(
            (row) => !shown && onlyShownViewId(row.value, writePoolSet) === viewId,
        );
        const lastShownIds = new Set(lastShown.map((row) => row.rowId));
        const writable = pending.filter((row) => !lastShownIds.has(row.rowId));
        return {
            viewId,
            shown,
            updates: writable.map((row) => ({
                rowId: row.rowId,
                next: composeVisibilityCellWrite({
                    current: row.value,
                    writePool,
                    viewId,
                    next: {
                        shown,
                        memberRows: row.memberRows
                            ? resolveVisibilityCellState(row.value, viewId).memberRows
                            : undefined,
                    },
                }),
            })),
            alreadyInStateCount: alreadyInState.length,
            lastShownViewCount: lastShown.length,
            hiddenColumnCount: hiddenColumnRows.length,
            shownEverywhereCount: shown
                ? 0
                : writable.filter((row) => (row.value.visibilityTypes ?? []).length === 0).length,
        };
    };

    const applyBulk = (plan: VisibilityMatrixBulkPlan) => {
        if (plan.updates.length === 0) return;
        onBulkChange(plan.updates);
        setSelectedRowIds(new Set<string>());
    };

    const showAllSwitch = (
        <Switch
            size="small"
            checked={showAllViews}
            onChange={setShowAllViews}
            disabled={controlsDisabled || focusColumn !== undefined || otherViewIds.size === 0}
            aria-label={`Show all views: ${ariaLabel}`}
        />
    );

    return (
        <FlexCol gap={5} aria-label={ariaLabel}>
            <FlexBox gap={10} justifyStart noGrow>
                <Text type="secondary">{readout}</Text>
                {otherViewIds.size > 0 ? (
                    showAllSwitch
                ) : (
                    <Tooltip title="Every view the organization defines is already a column.">
                        <span>{showAllSwitch}</span>
                    </Tooltip>
                )}
                <Text type="secondary">Show all views</Text>
            </FlexBox>
            {!showAllViews && storedOtherCount > 0 && (
                <Text type="warning">
                    {`${storedOtherCount} ${storedOtherCount === 1 ? 'view' : 'views'} from other form types ${storedOtherCount === 1 ? 'is' : 'are'} named here but not shown.`}
                </Text>
            )}
            {poolUnavailable && (
                <Alert
                    type="error"
                    showIcon
                    title="The organization's view list could not be loaded, so only the built-in views are listed. Editing stays disabled until it loads."
                />
            )}
            <div
                role="table"
                aria-label={`${ariaLabel} grid`}
                style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    backgroundColor: token.colorBgContainer,
                    overflow: 'auto',
                    maxHeight: token.controlHeight * 16,
                }}
            >
                <div style={{ minWidth: 'max-content' }}>
                    <VisibilityMatrixHeader
                        columns={displayedColumns}
                        gridTemplateColumns={gridTemplateColumns}
                        labelColumnWidth={labelColumnWidth}
                        selectedCount={selectedRows.length}
                        focusViewId={focusViewId}
                        onFocusViewChange={onFocusViewChange}
                        planBulk={planBulk}
                        onApplyBulk={applyBulk}
                        disabled={controlsDisabled}
                    />
                    {groups.map((group) => (
                        <div key={group.label}>
                            <div
                                role="row"
                                style={{
                                    backgroundColor: token.colorFillQuaternary,
                                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                }}
                            >
                                <div
                                    role="cell"
                                    style={{
                                        position: 'sticky',
                                        left: 0,
                                        width: 'max-content',
                                        padding: `${token.paddingXXS}px ${token.paddingSM}px`,
                                    }}
                                >
                                    <Text strong>{group.label}</Text>
                                </div>
                            </div>
                            {group.rows.map((row) => (
                                <div
                                    key={row.rowId}
                                    role="row"
                                    ref={row.rowId === focusRowId ? scrollFocusRowIntoView : undefined}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns,
                                        backgroundColor:
                                            row.rowId === focusRowId ? token.controlItemBgActive : undefined,
                                    }}
                                >
                                    <div
                                        role="cell"
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1,
                                            width: labelColumnWidth,
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: token.marginXXS,
                                            padding: `${token.paddingXXS}px ${token.paddingSM}px`,
                                            backgroundColor:
                                                row.rowId === focusRowId
                                                    ? token.controlItemBgActive
                                                    : token.colorBgContainer,
                                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedRowIds.has(row.rowId)}
                                            disabled={controlsDisabled}
                                            onChange={() => toggleRowSelection(row.rowId)}
                                            aria-label={`Select ${row.groupLabel}: ${row.label} (${row.rowId})`}
                                        />
                                        <Text>{row.label}</Text>
                                    </div>
                                    {displayedColumns.map((column) => (
                                        <VisibilityMatrixCell
                                            key={column.view.id}
                                            rowLabel={row.label}
                                            groupLabel={row.groupLabel}
                                            rowId={row.rowId}
                                            value={row.value}
                                            viewId={column.view.id}
                                            viewLabel={column.view.shortLabel}
                                            state={resolveVisibilityCellState(row.value, column.view.id)}
                                            memberRows={row.memberRows}
                                            objectKind={row.objectKind}
                                            formTypeId={formTypeId}
                                            disabled={controlsDisabled}
                                            onChange={(next) => onChange(row.rowId, next)}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <VisibilityMatrixLegend />
        </FlexCol>
    );
};
